import { useEffect, useState } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import type { useCreateKbServerAppLinkIdQueryJsmCardQuery } from './__generated__/useCreateKbServerAppLinkIdQueryJsmCardQuery.graphql.ts';

export const useCreateKbServerAppLinkId = ({
	cloudId,
	projectIdentifier,
}: {
	cloudId: string;
	projectIdentifier: string | null;
}): string | null => {
	const [appLinkId, setAppLinkId] = useState<string | null>(null);
	const relayEnvironment = useRelayEnvironment();

	useEffect(() => {
		let isMounted = true;

		const fetchAppLinkId = async () => {
			if (!projectIdentifier) {
				if (isMounted) {
					setAppLinkId(null);
				}
				return;
			}

			try {
				const response = await fetchQuery<useCreateKbServerAppLinkIdQueryJsmCardQuery>(
					relayEnvironment,
					graphql`
						query useCreateKbServerAppLinkIdQueryJsmCardQuery(
							$projectIdentifier: String!
							$cloudId: ID!
						) {
							knowledgeBase_confluenceAppLinkDetails(
								projectIdentifier: $projectIdentifier
								cloudId: $cloudId
							) {
								__typename
								... on KnowledgeBaseConfluenceAppLinkDetails {
									appLinkId
								}
							}
						}
					`,
					{
						projectIdentifier,
						cloudId,
					},
					{
						fetchPolicy: 'store-or-network',
					},
				).toPromise();

				const nextAppLinkId =
					response?.knowledgeBase_confluenceAppLinkDetails?.__typename ===
					'KnowledgeBaseConfluenceAppLinkDetails'
						? response.knowledgeBase_confluenceAppLinkDetails.appLinkId
						: null;

				if (isMounted) {
					setAppLinkId(nextAppLinkId ?? null);
				}
			} catch (error) {
				logException({
					exception: error,
					name: 'CreateKBCardServerLinkStatusQueryError',
					tags: {
						projectIdentifier,
						cloudId,
					},
				});
			}
		};

		void fetchAppLinkId();

		return () => {
			isMounted = false;
		};
	}, [cloudId, projectIdentifier, relayEnvironment]);

	return appLinkId;
};
